import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useUpdateCustomer } from '../hooks/use-customer-mutations';
import {
  customerEmailSchema,
  type CustomerEmailFormInput,
  type CustomerEmailFormOutput,
} from '../schemas/customer-form-schema';
import { customerEmailTaken } from '../schemas/customer-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['email']);

export interface ChangeEmailDialogProps {
  storeId: string;
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The email is the shopper's sign-in, so the dialog says so before it changes. */
export function ChangeEmailDialog({
  storeId,
  customer,
  open,
  onOpenChange,
}: ChangeEmailDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const update = useUpdateCustomer(storeId, 'customers:toasts.emailChanged');
  const form = useForm<CustomerEmailFormInput, unknown, CustomerEmailFormOutput>({
    resolver: useI18nResolver(customerEmailSchema),
    defaultValues: { email: customer.email },
  });

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    if (values.email.toLowerCase() === customer.email.toLowerCase()) {
      close();
      return;
    }
    try {
      await update.mutateAsync({ id: customer.id, body: { email: values.email } });
      close();
    } catch (error) {
      if (customerEmailTaken(error)) {
        form.setError('email', { type: 'server', message: t('customers:errors.emailTaken') });
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('customers:changeEmail.title')}</DialogTitle>
          <DialogDescription>
            {customer.hasPassword
              ? t('customers:changeEmail.descriptionAccount')
              : t('customers:changeEmail.descriptionGuest')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:changeEmail.field')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('customers:changeEmail.current', { email: customer.email })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('customers:changeEmail.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
